import type { ReactNode } from 'react'
import { LogOut, ShieldOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { XtellarMark } from '@/src/components/XtellarMark'
import { HELM_SUPPORT_EMAIL, useCustomerSuspended } from '@/src/lib/account-suspension'

/** What a suspended customer sees instead of pages that would only fail. */
export function AccountSuspendedScreen({ onSignOut }: { onSignOut: () => void }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <Card className="w-full max-w-md rounded-lg">
        <CardHeader className="space-y-3">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <XtellarMark />
            <span className="text-sm font-semibold uppercase tracking-[0.14em]">Helm</span>
          </div>
          <div className="flex justify-center">
            <ShieldOff className="h-8 w-8 text-destructive" aria-hidden="true" />
          </div>
          <CardTitle className="text-center">
            <h1 className="text-2xl font-semibold">Account suspended</h1>
          </CardTitle>
          <CardDescription className="text-center">
            Your Xtellar account is suspended, so your consignments, requests and parcels are not
            available in Helm.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center text-sm">
          To find out why, or to restore access, contact{' '}
          <a
            href={`mailto:${HELM_SUPPORT_EMAIL}`}
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            {HELM_SUPPORT_EMAIL}
          </a>
          .
        </CardContent>
        <CardFooter className="justify-center">
          <Button variant="outline" onClick={onSignOut}>
            <LogOut className="h-4 w-4" />
            Sign out
          </Button>
        </CardFooter>
      </Card>
    </main>
  )
}

/**
 * Shows the suspended screen in place of everything inside it once the API
 * refuses this login with CUSTOMER_SUSPENDED, from whichever request first.
 */
export function CustomerSuspensionBoundary({
  children,
  onSignOut,
}: {
  children: ReactNode
  onSignOut: () => void
}) {
  const suspended = useCustomerSuspended()
  return suspended ? <AccountSuspendedScreen onSignOut={onSignOut} /> : <>{children}</>
}
